/**
 * Cell rendering shared by Block and EnvelopeInspector.
 *
 * The wire carries a number two ways: a raw `Figure` on a `Row`
 * (`envelope.rows[i].figures[name]`) and a projected cell inside a block
 * payload (`_cell()` in streaming/emitter.py), which flattens the same shape
 * into `row.cells[column]`. Both are handled here so neither renderer has to
 * re-implement "never coerce a figure to a JS number" on its own — `value`
 * stays a string from the wire to the screen, which is what keeps a Decimal
 * rupee figure from ever passing through a binary float.
 */
import type { Figure } from '@/features/mitra/api/frames'
import type { Lang } from '@/features/mitra/lib/copy'

/** The shape `_cell()` produces for a figures-backed column. */
export interface ProjectedFigure {
  value: string | null
  unit: string
  scale?: string
  period?: string | null
  inferred: boolean
  /** Set when the source has no value at all — distinct from a zero. */
  unknown?: boolean
}

export type CellValue = string | boolean | null | undefined | ProjectedFigure

function isFigureLike(v: unknown): v is ProjectedFigure | Figure {
  return typeof v === 'object' && v !== null && 'value' in v
}

const EM_DASH = '—'

/**
 * Where the first separator falls, for a figure that counts something.
 *
 * A thousand, and the guard against mangling a year is `groupable` below
 * rather than this number. The floor was 10,000 to keep BS 2081 from reading
 * as "2,081", and it bought that at the price of an inconsistency inside one
 * card: a fee ledger drew `62,450.00` grouped and `1123.50` bare, two lines
 * apart, which reads as one of them being a different kind of number.
 */
const GROUP_FLOOR = 1_000

/**
 * Whether a figure's unit is one that counts, and therefore groups.
 *
 * MONEY, QUANTITIES AND LEVELS. A percentage, a ratio and a P/E are read
 * whole and never reach four digits meaningfully; a year, a period or an
 * identifier is not a quantity at all, and a separator inside one states a
 * magnitude nobody claimed. Keying on the unit rather than on the magnitude
 * is what lets the floor drop to a thousand without `2081` ever becoming
 * `2,081` — a year is not NPR and not shares.
 *
 * An index level groups for the same reason money does and for none of the
 * reasons a year does not: NEPSE at 2,557.31 is a magnitude, it is read that
 * way everywhere it is written, and `readable_figure` groups it server-side
 * so the sentence under the card matches the card. What it does NOT take is
 * an affix — a level is not an amount, and `unitPrefix` below is where that
 * is said.
 */
function groupable(cell: CellValue | Figure): boolean {
  if (!isFigureLike(cell)) return false
  return cell.unit === 'NPR' || cell.unit === 'shares' || cell.unit === 'index'
}

/**
 * A figure's digits, grouped the way this domain writes them.
 *
 * TWO-TWO-THREE, NOT THREE-THREE-THREE. `calc/speech.py` holds the
 * convention — `_GROUPS = ((10**7, "crore"), (10**5, "lakh"), (10**3,
 * "thousand"))` — and `guardrails/stages.py` says why `million` is absent
 * from it: "nothing in this domain counts that way, the register and the fee
 * tables are written in lakh and crore". So a NEPSE volume is 1,06,26,370
 * and never 10,626,370, and the Western grouping a `toLocaleString()` would
 * have produced is not a neutral default here — it is the wrong number of
 * digits in the wrong places for the reader this is drawn for.
 *
 * PURELY LEXICAL, AND THAT IS THE POINT. It splits the string the engine
 * sent and inserts separators between digits it never reads as a number.
 * Nothing is parsed, so nothing is re-precisioned: `"11.50"` keeps its
 * trailing zero, a value past 2^53 keeps every digit, and the fraction is
 * returned exactly as it arrived. That is what separates this from the
 * re-formatting the whole surface refuses — a separator changes how digits
 * are spaced, never which digits they are.
 */
export function grouped(value: string): string {
  const m = /^([-\u2212]?)(\d+)(\.\d+)?$/.exec(value.trim())
  // Anything that is not a bare decimal — a range, a ratio, an already
  // grouped string, a date — is returned untouched rather than guessed at.
  if (!m) return value
  const [, hyphen = '', whole = '', fraction = ''] = m
  // A loss takes the true minus (U+2212), never a hyphen: the finance look
  // (2026-10-13) and the engine's own `figure_text` write it so.
  const sign = hyphen ? '\u2212' : ''
  if (whole.length < String(GROUP_FLOOR).length) return `${sign}${whole}${fraction}`
  const last3 = whole.slice(-3)
  const rest = whole.slice(0, -3)
  const head = rest.replace(/\B(?=(\d{2})+$)/g, ',')
  return `${sign}${head},${last3}${fraction}`
}

/**
 * The text for any cell or figure the wire can send. Always the original
 * string the engine sent — an honest unknown renders as a dash, never as 0,
 * because a zero is a different claim than "we don't know."
 */
/** An ISO date on its own: a whole cell, never a substring of a sentence. */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

/**
 * A date the way a reader says it, from a cell that stores one.
 *
 * The engine keeps a row's date field ISO on purpose — it is a machine field
 * and `calc/dates.py`'s note says the CLIENT formats it. Nothing did, so
 * "REPORTED PERIOD 2026-07-21" was drawn on a card, which is the same
 * database spelling the prose rule refuses one surface over. Reported off the
 * running widget as "isn't this table design old?", and the ISO date is the
 * strongest tell in the picture.
 *
 * EXPORTED SINCE 2026-09-11, for the passages card's period. That shape is
 * not a table cell and so never reached `cellText`, and it printed a bare
 * `2023-11-23` under a headline -- the same database spelling on the one card
 * whose whole job since `get_news` started drawing through it is to say how
 * old an article is.
 *
 * ANCHORED, SO ONLY A WHOLE CELL MATCHES. A date inside a longer string is
 * somebody's sentence and is left alone; a cell that IS a date is a stored
 * value being displayed. The parts are read as text and re-spelled — nothing
 * is parsed into a Date, which would pull the reader's timezone into a NEPSE
 * fact, and an impossible month or day is returned untouched rather than
 * guessed at.
 */
export function asAReaderWritesDates(value: string): string {
  const m = ISO_DATE.exec(value)
  if (!m) return value
  const [, year = '', rawMonth = '', rawDay = ''] = m
  const month = Number(rawMonth)
  const day = Number(rawDay)
  const name = MONTHS[month - 1]
  if (name === undefined || day < 1 || day > 31) return value
  return `${String(day)} ${name} ${year}`
}

export function cellText(cell: CellValue | Figure): string {
  if (cell === null || cell === undefined) return EM_DASH
  if (typeof cell === 'boolean') return cell ? 'true' : 'false'
  if (typeof cell === 'string') return asAReaderWritesDates(cell)
  if (isFigureLike(cell)) return cell.value === null ? EM_DASH : cell.value
  return String(cell)
}

/** Whether a cell is a figure the engine derived rather than read directly. */
export function cellInferred(cell: CellValue | Figure): boolean {
  return isFigureLike(cell) && cell.inferred
}

/** Whether a cell carries any numeric/engine value at all — used to pick a mono style. */
export function cellIsFigure(cell: CellValue | Figure): boolean {
  return isFigureLike(cell)
}

/**
 * The unit as a reader sees it, on the side a reader expects it.
 *
 * A number without its unit is the defect `Figure` exists to prevent, and the
 * table got away with omitting it only because the column heading carried it
 * — `price` over a column of rupees. The shapes beside the table have no
 * heading row, so each figure has to say what it is where it stands.
 *
 * CURRENCY LEADS AND EVERYTHING ELSE TRAILS. "NPR 31,594.20" is how the
 * amount is written in Nepal, and it is already how this engine writes one in
 * prose — `agent/prompt.py` and every eval answer say "Rs 524.50", never
 * "524.50 Rs". A card that trailed the currency put the same amount in two
 * orders on one screen, the figure above the sentence disagreeing with the
 * sentence about where its own unit goes.
 *
 * `ratio` and `count` take nothing: a correlation of -0.46 is a pure number
 * and "-0.46 ratio" is not how anyone reads one. That is a choice about an
 * affix and not a dropped unit — the value keeps its `unit` field the whole
 * way, and the shape that draws it knows which axis it is on.
 *
 * `index` takes nothing either, and here that is not a style choice: NEPSE is
 * 2,557.31, full stop. There is no "pts" anywhere in this product and
 * inventing one would be a second wrong unit replacing the first. The card
 * header already names the instrument, which is the fact a reader needs.
 */
export function unitPrefix(cell: CellValue | Figure): string {
  if (!isFigureLike(cell) || cell.value === null) return ''
  return cell.unit === 'NPR' ? 'NPR ' : ''
}

/**
 * A figure's unit as the wire named it, or "" for a cell that carries none.
 *
 * `unitPrefix` and `unitSuffix` answer "how is this one figure affixed"; this
 * answers "is this the same kind of quantity as that one", which is the
 * question an axis has to ask before it may state a unit at all. A chart
 * whose points disagree is two scales drawn as one, and the honest label for
 * it is none — so the comparison lives here, on the wire's own string, rather
 * than in a renderer inferring a unit back out of an affix.
 */
export function cellUnit(cell: CellValue | Figure): string {
  return isFigureLike(cell) && cell.value !== null ? cell.unit : ''
}

/**
 * The magnitude word for a figure's `scale`, or "" for `Scale.ONE`.
 *
 * Copied from `calc/speech.py`'s `_ENGLISH.scales` rather than invented —
 * `{"thousand": "thousand", "lakh": "lakh", "crore": "crore", "million":
 * "million", "billion": "billion"}` — because this is the one word standing
 * between a card and the spoken answer disagreeing about a figure's
 * magnitude by a factor of a thousand or worse. `Bottlers Nepal`'s market cap is `215898.00` at
 * `scale=million`: spoken as "215,898 million rupees" and, before this
 * existed, drawn as `NPR 2,15,898.00` with the multiplier silently dropped —
 * a reader sees roughly two lakh where the answer means NPR 215.9 billion.
 *
 * A `ProjectedFigure` may omit `scale` on the wire (unlike `Figure`, where it
 * is required); absent reads the same as `Scale.ONE`, its Python default.
 */
const SCALE_WORDS: Record<string, string> = {
  thousand: 'thousand',
  lakh: 'lakh',
  crore: 'crore',
  million: 'million',
  billion: 'billion',
}

function scaleWord(cell: CellValue | Figure): string {
  if (!isFigureLike(cell) || cell.value === null) return ''
  const scale = cell.scale ?? 'one'
  return scale === 'one' ? '' : (SCALE_WORDS[scale] ?? '')
}

/**
 * The magnitude and unit words that trail a figure's digits.
 *
 * SCALE LEADS, THE UNIT'S OWN NOUN FOLLOWS — the same order `spoken_figure`
 * reads in: "two lakh fifteen thousand eight hundred ninety eight million
 * rupees" is scale-then-noun, never the reverse, and a card that put them the
 * other way would read differently from how it sounds. `NPR` has no noun of
 * its own here (`unitPrefix` already carries the currency), so a scaled
 * rupee figure's whole suffix is the magnitude word alone — `unitSuffix`
 * itself, "" for one, or " million" and the like.
 */
export function unitSuffix(cell: CellValue | Figure): string {
  if (!isFigureLike(cell) || cell.value === null) return ''
  const scale = scaleWord(cell)
  switch (cell.unit) {
    case 'percent':
      return `${scale ? ` ${scale}` : ''}%`
    case 'shares':
      return ` ${scale ? `${scale} ` : ''}shares`
    case 'days':
      return ` ${scale ? `${scale} ` : ''}days`
    default:
      return scale ? ` ${scale}` : ''
  }
}

/**
 * Which way a figure points, for the one thing colour is spent on here.
 *
 * Parsed, and it never reaches the screen as a number — the text beside it is
 * always the wire's own string. `Rail.tsx` reads a sign the same way and says
 * why: "a direction is not a figure — it is which side of the zero line the
 * bar is drawn on". Zero is `flat` and takes no colour, because level is not
 * a small amount of good news.
 */
export function cellDirection(cell: CellValue | Figure): 'up' | 'down' | 'flat' {
  if (!isFigureLike(cell) || cell.value === null) return 'flat'
  const parsed = Number(cell.value)
  if (!Number.isFinite(parsed) || parsed === 0) return 'flat'
  return parsed > 0 ? 'up' : 'down'
}

/** A figure as a number, for positioning a rectangle and nothing else. */
export function cellNumber(cell: CellValue | Figure): number {
  if (!isFigureLike(cell) || cell.value === null) return 0
  const parsed = Number(cell.value)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * A holdings card names its cells with the figure's own key — `unrealised_pct`,
 * `weight_pct`, `wacc` — and a machine name in front of a retail reader is the
 * defect this fixes: UNREALISED_PCT and unrealised_gain are not words anyone
 * says. Map the keys a card actually carries to plain ones, and de-underscore
 * anything unmapped so a raw key can never reach the screen. Presentation
 * only: the server still sends the key, the projection still keys off it; this
 * is the last step before the label is drawn.
 */
const FIGURE_LABELS: Record<string, string> = {
  unrealised_pct: 'Unrealised',
  unrealised_gain: 'Unrealised gain',
  weight_pct: 'Weight',
  your_weight: 'Your weight',
  market_percent: 'NEPSE weight',
  wacc: 'Avg cost',
  wacc_stated: 'Stated cost',
  cost: 'Cost',
  price: 'Price',
  value: 'Value',
  quantity: 'Qty',
  change_pct: 'Change',
  volume: 'Volume',
  pe: 'P/E',
  // THE REST OF WHAT A COMPARISON PUTS IN A ROW. Captured from the running
  // widget: a NABIL/SANIMA grid drew `pb`, `eps`, `market cap`, `book value`
  // and `roe` down its left edge — the column names, in front of a reader,
  // which is the one thing this map exists to stop. `pe` was here and its five
  // neighbours were not, so the one row a reader recognised was the one
  // somebody had needed before. The design's own comparison rows read "P/E",
  // "Dividend, 3-yr avg", "NPL ratio", "Capital adequacy": names, not columns.
  //
  // Cased as this domain writes them — P/B and ROE are read as letters, and
  // "Market cap" is a phrase rather than an initialism.
  pb: 'P/B',
  eps: 'EPS',
  roe: 'ROE',
  market_cap: 'Market cap',
  book_value: 'Book value',
  dividend_yield_pct: 'Div yield',
  // The three figures a `bars` block can carry — `_BAR_FIGURES` in
  // streaming/emitter.py, which is a closed list. Without these the legend
  // printed `cash_pct`, which is the one thing this map exists to stop, and
  // bars was the only shape on this surface that never called `humanLabel`.
  // The words are the engine's own rather than this file's: `get_dividends`
  // is specified as "Cash and bonus dividend per fiscal year", and the
  // knowledge base's `dividend-en` is "Cash dividend versus bonus shares".
  cash_pct: 'Cash dividend',
  bonus_pct: 'Bonus shares',
  correlation: 'Correlation',
  // `analyse_performance`'s two plotted measures. `PAID_IN` was on screen as
  // the raw key, in a legend, beside `VALUE` -- the machine name in front of
  // a retail reader that this map exists to stop. The words are the tool's
  // own: its `__window__` row calls the figure `paid_in` and its docstring
  // calls it "the cash paid in by then".
  paid_in: 'Paid in',
  // The NEPSE index card's own rows (`charts/Facts.tsx`), moved here so the
  // card names them through this map in every register.
  previous_close: 'Previous close',
  day_high: "Day's high",
  day_low: "Day's low",
  below_52_week_high: 'Points below 52-week high',
  below_52_week_high_pct: 'Below 52-week high',
  above_52_week_low: 'Points above 52-week low',
  above_52_week_low_pct: 'Above 52-week low',
  week_52_range: '52-week range',
  // The legacy peer card's group mark when the group states its middle value.
  group_median: 'Group median',
}

/**
 * The same labels in the two Nepali registers, for the keys a card is read
 * by. A key absent here draws the English above: initialisms (P/E, EPS, ROE)
 * are read as letters in every register, and a Nepali card with "P/E" on it
 * is the channel's own habit.
 *
 * The Devanagari follows the engine's own card table
 * (`streaming/captions.py`'s `FIGURE_LABELS["devanagari"]`), so a label reads
 * the same whether the server or this side named the figure; the romanized
 * entries are that table read aloud in the panel's romanization.
 *
 * TODO(speaker review): drafted 2026-10-05 (wow retest 2: stock-card labels
 * stayed English in a Devanagari answer). A Nepali speaker has not read them.
 */
const FIGURE_LABELS_BY_LANG: Record<Exclude<Lang, 'en'>, Record<string, string>> = {
  ne: {
    unrealised_pct: 'नाफा/नोक्सान %',
    unrealised_gain: 'नाफा/नोक्सान',
    weight_pct: 'हिस्सा',
    your_weight: 'तपाईंको हिस्सा',
    market_percent: 'NEPSE मा हिस्सा',
    wacc: 'औसत लागत',
    cost: 'लागत',
    price: 'मूल्य',
    value: 'बजार मूल्य',
    quantity: 'कित्ता',
    change_pct: 'परिवर्तन',
    change: 'परिवर्तन',
    volume: 'कारोबार कित्ता',
    previous_close: 'अघिल्लो बन्द मूल्य',
    day_high: 'दिनको उच्च',
    day_low: 'दिनको न्यून',
    market_cap: 'बजार पूँजीकरण',
    book_value: 'प्रति सेयर नेटवर्थ',
    cash_pct: 'नगद लाभांश',
    bonus_pct: 'बोनस सेयर',
    paid_in: 'लगानी गरिएको',
    below_52_week_high: '52 हप्ताको उच्चभन्दा तल (अङ्क)',
    below_52_week_high_pct: '52 हप्ताको उच्चभन्दा तल',
    above_52_week_low: '52 हप्ताको न्यूनभन्दा माथि (अङ्क)',
    above_52_week_low_pct: '52 हप्ताको न्यूनभन्दा माथि',
    week_52_range: '52 हप्ताको दायरा',
    group_median: 'समूहको मध्यक',
  },
  'ne-latn': {
    unrealised_pct: 'Nafa/noksan %',
    unrealised_gain: 'Nafa/noksan',
    weight_pct: 'Hissa',
    your_weight: 'Tapaiko hissa',
    market_percent: 'NEPSE ma hissa',
    wacc: 'Ausat lagat',
    cost: 'Lagat',
    price: 'Mulya',
    value: 'Bajar mulya',
    quantity: 'Kitta',
    change_pct: 'Parivartan',
    change: 'Parivartan',
    volume: 'Karobar kitta',
    previous_close: 'Aghillo banda mulya',
    day_high: 'Dinko uchcha',
    day_low: 'Dinko nyun',
    market_cap: 'Bajar punjikaran',
    book_value: 'Prati share networth',
    cash_pct: 'Nagad labhansh',
    bonus_pct: 'Bonus share',
    paid_in: 'Lagani gariyeko',
    below_52_week_high: '52 hapta ko uchcha bhanda tala (ank)',
    below_52_week_high_pct: '52 hapta ko uchcha bhanda tala',
    above_52_week_low: '52 hapta ko nyun bhanda mathi (ank)',
    above_52_week_low_pct: '52 hapta ko nyun bhanda mathi',
    week_52_range: '52 hapta ko dayara',
    group_median: 'Samuha ko madhyak',
  },
}

/**
 * Whether a figure has a reader's label here, rather than its storage name
 * read aloud ("Nepse change pct"). A surface that has no server words for a
 * figure draws only these (My board before the engine sent `facts`).
 */
export function hasFigureLabel(key: string): boolean {
  return key.toLowerCase().replace(/ /g, '_') in FIGURE_LABELS
}

/**
 * A figure's label in the answer's register. `lang` defaults to English so a
 * caller that has no register to hand draws what it always drew.
 */
export function humanLabel(key: string | null | undefined, lang: Lang = 'en'): string {
  if (!key) return ''
  // EITHER FORM OF THE KEY, because the server sends the spaced one. The
  // four part builders in `streaming/emitter.py` -- facts, tiles, grid and
  // comparison -- label a part `name.replace("_", " ")`, so what reaches a
  // draw site is "weight pct" and never "weight_pct". Looking up only the
  // underscored key meant every one of those labels missed this map and fell
  // through to itself, and a position card drew `WEIGHT PCT` and `CHANGE PCT`
  // at a reader. The words were already here; the lookup could not reach them.
  //
  // Idempotent on purpose: a component may be handed a raw key today and an
  // already-named label tomorrow, and "Avg cost" must survive a second pass.
  const wanted = key.toLowerCase().replace(/ /g, '_')
  const own = lang === 'en' ? undefined : FIGURE_LABELS_BY_LANG[lang][wanted]
  return own ?? FIGURE_LABELS[wanted] ?? key.replace(/_/g, ' ')
}

/**
 * A cell's text with its digits grouped, where grouping is what a reader of
 * that unit expects.
 *
 * The one entry point a renderer needs: `cellText` alone under-reads a large
 * quantity, and `grouped` alone would put a separator inside a year.
 */
export function groupText(cell: CellValue | Figure): string {
  const text = cellText(cell)
  if (groupable(cell)) return grouped(text)
  // A signed figure that does not group (a percent) still takes the true
  // minus; only a bare decimal is touched, never a range or a date.
  return /^-\d+(\.\d+)?$/.test(text) ? `\u2212${text.slice(1)}` : text
}

/**
 * A chart caption, with the figure named the reader's way.
 *
 * Captured from the running widget: a dividend chart was headed "CASH PCT BY
 * FISCAL YEAR". `cash_pct` is how `mitra_v1` stores it; "cash dividend" is
 * what it is, and the words were already in `FIGURE_LABELS`.
 *
 * `_trend_parts` composes this server-side as `f"{measure} by {axis}"` with
 * underscores swapped for spaces, then joins any notes with " · ". So what
 * arrives is a phrase, not a key, and only the part before " by " is a column
 * name. Both halves go through `humanLabel` — which is why it takes the spaced
 * form — and a phrase with no " by " in it is a note and is left alone.
 *
 * NAMED HERE AND NOT ON THE SERVER, because `FIGURE_LABELS` is the one place
 * this product keeps reader-facing words for figures, and a second copy in
 * Python would be a map to keep in step by hand.
 */
export function captionLabel(caption: string): string {
  // The server's notes arrive joined " · "; they are said joined by a comma,
  // never the middle dot (owner's rule, 2026-10-31).
  const [head = '', ...rest] = caption.split(/\s*·\s*/u)
  const at = head.indexOf(' by ')
  if (at < 0) return [head, ...rest].join(', ')
  const named = `${humanLabel(head.slice(0, at))} by ${humanLabel(head.slice(at + 4))}`
  return [named, ...rest].join(', ')
}
